// ---------------------------------------------------------------------------
// PostHog — analítica de producto por usuario.
//
// Firebase Analytics (ver utils/analytics.ts) responde "cuánta gente hizo X".
// PostHog responde "qué hizo ESTE usuario", que es lo que Firebase/GA4 no puede
// darnos: perfil por persona, línea de tiempo de eventos, embudos y segmentación
// por propiedades arbitrarias. Los dos conviven: `utils/analytics.ts` escribe a
// ambos destinos y ningún proveedor puede romper al otro.
//
// Este módulo espeja la forma de `sentry.ts`: un único `initPostHog()` llamado
// desde main.tsx, sin React, y no-op silencioso si falta la key.
//
// La identidad del usuario NO se resuelve aquí — vive en analytics/identity.ts,
// enganchada al store de zustand.
// ---------------------------------------------------------------------------
import posthog from "posthog-js";
import { APP_VERSION } from "./constants/appVersion";
import { getDisplayMode } from "./utils/displayMode";
import { definedOnly, describeHardware } from "./utils/diagnosticsContext";

const DEFAULT_API_HOST = "https://us.i.posthog.com";
const DEFAULT_UI_HOST = "https://us.posthog.com";

// Dominios cuyo dominio registrable realmente controlamos y donde sí se puede
// escribir una cookie compartida entre subdominios.
//
// OJO: *.web.app y *.firebaseapp.com están en la Public Suffix List, así que una
// cookie con Domain=".web.app" es descartada por todos los navegadores. Activar
// `cross_subdomain_cookie` ahí no falla con error: simplemente no hace nada y
// esconde el problema. Hoy la app se sirve desde *.web.app
// (tdc-development-846de / central-gps-production), asi que la bandera queda
// correctamente en false. El dia que se sirva desde dumaxst.com se activa sola,
// sin tocar codigo.
const SHARED_COOKIE_DOMAIN = /(^|\.)dumaxst\.com$/i;

let enabled = false;

/** ¿Se inicializó PostHog? Falso si no había key configurada. */
export const isPostHogEnabled = (): boolean => enabled;

/**
 * ¿PostHog tiene una persona identificada ahora mismo?
 *
 * Se consulta al SDK, no a una variable de módulo: la identidad de PostHog vive
 * en localStorage+cookie y SOBREVIVE a una recarga, mientras que cualquier
 * estado en memoria muere con la página. Sin esto, un logout que no alcanza a
 * ejecutarse antes de una redirección deja al siguiente usuario heredando el
 * distinct_id del anterior.
 */
export const isPostHogIdentified = (): boolean => {
  if (!enabled) return false;
  try {
    return posthog._isIdentified();
  } catch {
    return false;
  }
};

/**
 * Propiedades que se adjuntan a TODOS los eventos de la sesión.
 *
 * Se registran al init y hay que volver a registrarlas después de cada
 * `posthog.reset()`, porque reset() también las borra.
 */
function registerStaticSuperProperties(): void {
  posthog.register({
    app: "v5",
    app_version: APP_VERSION,
    tenant: import.meta.env.VITE_THEME || "unknown",
    environment: import.meta.env.VITE_ENVIRONMENT || "development",
    display_mode: getDisplayMode(),
    ...definedOnly(describeHardware()),
    // typeof-guard: __IS_PREVIEW__ es un `define` de vite.config.ts, y
    // vitest.config.ts es un archivo aparte que no lo declara.
    deploy_channel:
      typeof __IS_PREVIEW__ !== "undefined" && __IS_PREVIEW__
        ? "preview"
        : "standard",
  });
}

/**
 * Normaliza un pathname a un patrón de baja cardinalidad.
 *
 * La app tiene ~190 rutas y casi todas llevan un :id, así que sin esto PostHog
 * acumula decenas de miles de `$pathname` distintos y los desgloses por página
 * quedan inservibles. Guardamos el `$current_url` real y añadimos el patrón.
 */
function isIdSegment(seg: string): boolean {
  // Numerico puro: /vehiculos/12345
  if (/^[0-9]+$/.test(seg)) return true;
  // UUID
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(seg)) {
    return true;
  }
  // Auto-id de Firestore: 20 caracteres de [A-Za-z0-9] con mayusculas y
  // minusculas mezcladas (p. ej. Ne6pwRmFkr4bArJN47NZ). Un regex hexadecimal
  // NO los atrapa: llevan letras fuera de a-f, que es justo el caso real de
  // esta app y el que hacia inutil la normalizacion.
  if (/^[A-Za-z0-9_-]{15,}$/.test(seg) && /[0-9]/.test(seg) && /[A-Za-z]/.test(seg)) {
    return true;
  }
  return false;
}

export function normalizePath(pathname: string): string {
  return pathname
    .split("/")
    .map((seg) => (isIdSegment(seg) ? ":id" : seg))
    .join("/");
}

export const initPostHog = (): void => {
  // La key se lee DENTRO de la función (a diferencia de sentry.ts, que la lee a
  // nivel de módulo) para que los tests puedan usar vi.stubEnv sin resetModules.
  const key = import.meta.env.VITE_POSTHOG_KEY;
  if (!key) {
    console.warn("PostHog key not configured. Product analytics disabled.");
    return;
  }

  posthog.init(key, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || DEFAULT_API_HOST,
    ui_host: DEFAULT_UI_HOST,

    // Autocapture es la razón de ser de este cambio: hoy el ÚNICO lugar
    // instrumentado a mano es el sidebar. Autocapture cubre las otras ~189
    // rutas gratis y de forma retroactiva.
    autocapture: true,

    // Apagado a propósito: AnalyticsProvider ya emite una vista por página
    // lógica con nombre legible. El modo automático dispara también en cada
    // cambio de query param (?tab=, ?page=), lo que inflaría vistas, sesiones
    // y factura varias veces.
    capture_pageview: false,
    // Explícito: el valor por defecto es 'if_capture_pageview', que al apagar
    // lo de arriba desactivaría $pageleave y perderíamos duración de sesión.
    capture_pageleave: true,

    // Solo los usuarios identificados crean perfil de persona. La app es 100%
    // autenticada, así que no perdemos nada y evitamos pagar perfil por cada
    // bot y cada rebote en /login.
    person_profiles: "identified_only",

    // Sentry ya graba replays (10% en prod). Un segundo grabador sería
    // redundante y caro.
    disable_session_recording: true,
    disable_surveys: true,

    // Explícito y a propósito: con solo cookie, el fallo de la cookie
    // cross-subdomain en *.web.app haría que se regenerara un distinct_id
    // anónimo en cada carga, inflando "usuarios únicos".
    persistence: "localStorage+cookie",
    cross_subdomain_cookie: SHARED_COOKIE_DOMAIN.test(window.location.hostname),

    // No enmascaramos todo: eso reduciría autocapture a "alguien hizo clic en
    // algún botón". El texto se recorta en before_send (ver abajo).
    mask_all_text: false,
    mask_all_element_attributes: false,

    before_send: (event) => {
      if (!event) return event;
      const props = event.properties as Record<string, unknown> | undefined;
      if (props) {
        if (typeof props.$pathname === "string") {
          props.path_pattern = normalizePath(props.$pathname);
        }
        // El texto capturado por autocapture en esta app puede contener placas,
        // nombres de conductores y de clientes. Lo recortamos: suficiente para
        // identificar el control, no para exportar datos.
        const elements = props.$elements as
          | Array<Record<string, unknown>>
          | undefined;
        if (Array.isArray(elements)) {
          for (const el of elements) {
            if (typeof el.$el_text === "string") {
              el.$el_text = el.$el_text.slice(0, 60);
            }
          }
        }
      }
      return event;
    },

    loaded: () => {
      if (import.meta.env.DEV) posthog.debug();
    },
  });

  enabled = true;
  registerStaticSuperProperties();
};

// --- Primitivas delgadas que nunca lanzan -----------------------------------
// utils/analytics.ts y analytics/identity.ts solo hablan con estas, para que un
// fallo de PostHog jamás pueda tumbar el camino de Firebase ni la app.

export const capturePostHogEvent = (
  name: string,
  props?: Record<string, unknown>,
): void => {
  if (!enabled) return;
  try {
    posthog.capture(name, props);
  } catch (error) {
    console.error("[PostHog] capture failed:", error);
  }
};

/**
 * Registra super propiedades (se adjuntan a los eventos siguientes).
 *
 * Deliberadamente NO usamos `posthog.setPersonProperties()` aquí: con
 * `person_profiles: "identified_only"`, llamarlo sobre un visitante anónimo lo
 * promueve a persona identificada y anula el ajuste. Las propiedades de persona
 * se fijan en un único lugar: `identifyPostHog`.
 */
export const registerPostHogProperties = (
  props: Record<string, unknown>,
): void => {
  if (!enabled) return;
  try {
    posthog.register(props);
  } catch (error) {
    console.error("[PostHog] register failed:", error);
  }
};

export interface PostHogIdentity {
  id: string;
  companyId?: string;
  companyName?: string;
  roleName?: string;
  roleId?: string;
  yardId?: string;
  emailDomain?: string;
  email?: string;
  name?: string;
}

export const identifyPostHog = (identity: PostHogIdentity): void => {
  if (!enabled) return;
  try {
    posthog.identify(
      identity.id,
      {
        // $set — estado actual; se sobrescribe en cada login para que siga al
        // usuario cuando cambia de rol, empresa o patio.
        company_id: identity.companyId,
        company_name: identity.companyName,
        role: identity.roleName,
        role_id: identity.roleId,
        yard_id: identity.yardId,
        email_domain: identity.emailDomain,
        // `email` y `name` son las propiedades con las que PostHog rotula la
        // ficha de una persona en su interfaz y por las que permite buscar.
        // Sin ellas la lista de personas es una columna de uids.
        ...(identity.email ? { email: identity.email } : {}),
        ...(identity.name ? { name: identity.name } : {}),
        app_version: APP_VERSION,
        tenant: import.meta.env.VITE_THEME || "unknown",
      },
      {
        // $set_once — atributos de adquisición que nunca se deben pisar.
        first_seen_at: new Date().toISOString(),
        initial_company_id: identity.companyId,
        initial_role: identity.roleName,
      },
    );

    // Analítica por empresa: retención, adopción y embudos por cliente sin
    // tener que filtrar a mano en cada insight.
    if (identity.companyId) {
      posthog.group("company", identity.companyId, {
        name: identity.companyName ?? identity.companyId,
      });
    }
  } catch (error) {
    console.error("[PostHog] identify failed:", error);
  }
};

/**
 * Corta el vínculo con la persona identificada y genera un id anónimo nuevo.
 *
 * Es lo que evita que, en una máquina compartida (caseta, despacho, turnos), el
 * siguiente usuario herede el distinct_id del anterior y sus eventos se fusionen
 * en la persona equivocada — una fusión que PostHog no permite deshacer.
 *
 * Usamos reset() y no reset(true): el segundo también rota el $device_id y
 * perderíamos la señal de "misma máquina física" sin ganar nada.
 */
export const resetPostHog = (): void => {
  if (!enabled) return;
  try {
    posthog.reset();
    // reset() también borra las super propiedades. Sin esto, cada evento
    // posterior al logout perdería tenant/environment/app_version y quedaría
    // imposible de filtrar.
    registerStaticSuperProperties();
  } catch (error) {
    console.error("[PostHog] reset failed:", error);
  }
};

/** Solo para tests: devuelve el módulo a su estado sin inicializar. */
export const _resetPostHogModuleForTests = (): void => {
  enabled = false;
};

export { posthog };
